import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/hooks/useAuthStore';
import { useTenantStore } from '@/hooks/useTenantStore';
import { useNDACompliance } from '@/hooks/useNDACompliance';
import { NDAAgreementModal } from '@/components/legal/NDAAgreementModal';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, AlertCircle } from 'lucide-react';

export default function NDAAcceptance() {
  const { user, loading: authLoading } = useAuthStore();
  const { tenantId } = useTenantStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);

  // Get company ID from location state or tenant store
  const companyId = (location.state as any)?.companyId || tenantId;

  const {
    needsAcceptance,
    loading,
    activeNDA,
    error,
    pendingCompanies,
    currentCompanyIndex,
    totalPendingCount,
  } = useNDACompliance(companyId || undefined);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Loading state
  if (!mounted || authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Loading agreement...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // No company selected
  if (!companyId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <AlertCircle className="h-12 w-12 text-warning mx-auto" />
            <h2 className="text-xl font-semibold">No Company Selected</h2>
            <p className="text-muted-foreground">Please select a company to continue.</p>
            <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
            <h2 className="text-xl font-semibold">Error Loading Agreement</h2>
            <p className="text-muted-foreground">{error}</p>
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already accepted or no NDA required
  if (!needsAcceptance || !activeNDA) {
    return <Navigate to="/dashboard" replace />;
  }

  // Get current company or first pending company
  const currentCompany =
    pendingCompanies.length > 0
      ? companyId
        ? pendingCompanies.find((c) => c.companyId === companyId) || pendingCompanies[0]
        : pendingCompanies[0]
      : null;

  if (!currentCompany) {
    return <Navigate to="/dashboard" replace />;
  }

  // Show NDA acceptance modal
  return (
    <NDAAgreementModal
      companyId={currentCompany.companyId}
      companyName={currentCompany.companyName}
      nda={activeNDA}
      progressInfo={{
        current: currentCompanyIndex + 1,
        total: totalPendingCount,
      }}
    />
  );
}
